import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import classes from './TurnstileWidget.module.scss';

export type Props = {
  /**
   * Please use useCallback for this to prevent rerenders.
   * @param token
   */
  onTokenReceived?: (token: string) => void;

  /**
   * A customer value that can be used to differentiate widgets under the same sitekey in analytics
   * and which is returned upon validation. This can only contain up to 32 alphanumeric characters
   * including _ and -.
   */
  action?: string;
};

export default function TurnstileWidget({ onTokenReceived, action }: Props) {
  const widgetElementRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [turnstileObject, setTurnstileObject] = useState<typeof window.turnstile | null>(null);

  useEffect(() => {
    if (turnstileObject && process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      if (widgetElementRef.current) {
        widgetIdRef.current = turnstileObject.render(widgetElementRef.current, {
          sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            onTokenReceived && onTokenReceived(token);
          },
          'error-callback': (error) => {
            if (window.newrelic) {
              window.newrelic.noticeError(new Error(`Turnstile error: ${error}`));
            }
          },
          theme: 'light',
          size: 'flexible',
          action: action,
        });
      }

      return () => {
        if (widgetIdRef.current) {
          turnstileObject.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }
  }, [widgetIdRef, turnstileObject, onTokenReceived, action]);

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onReady={() => {
          setTurnstileObject(window.turnstile);
        }}
      />
      <div ref={widgetElementRef} className={classes.widget} />
    </>
  );
}
